import React, { useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, functions } from '../../config/firebase'; // Ensure functions is exported from config
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import './supportDataDeletion.css';
import { ClipLoader } from 'react-spinners';

const SupportDataDeletion = () => {
    const [user] = useAuthState(auth);
    const [email, setEmail] = useState('');
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const deleteUserAccount = async () => {
         const confirmed = window.confirm(
            "WARNING: This will permanently wipe your profile, matches, and messages. This action cannot be undone. Are you sure?"
        );
        
        if (confirmed) {
            // For logged-in users, we can just delete directly via Auth if we wanted, 
            // likely re-auth is needed. But following the user request for email flow:
             setIsLoading(true);
             setError(null);
             setMessage(null);
             try {
                 const requestDeletionFn = httpsCallable(functions, 'requestDeletion');
                 const result = await requestDeletionFn({ email: user.email, reason: "User initiated from account" });
                 setMessage(result.data.message);
             } catch (err) {
                 console.error(err);
                 setError(err.message || "Failed to send deletion request.");
             } finally {
                 setIsLoading(false);
             }
        }
    };

    const handleRequestDeletion = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage(null);

        try {
            const requestDeletionFn = httpsCallable(functions, 'requestDeletion');
            // Check if functions is initialized? Usually it is in firebase config.
            // If not found, catch block will handle.
            const result = await requestDeletionFn({ email, reason });
            setMessage(result.data.message);
        } catch (err) {
            console.error("Deletion request failed:", err);
            setError("Failed to send request. Please ensure you entered a valid email.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='main support-deletion-page'>
             <a href="https://flintt.me/"><div className='nav-logo'></div></a>

            <div className="support-deletion">
                <h1>Data Deletion Request</h1>

                {/* Section 2: Subscription Warning (Crucial) */}
                <div className="warning-box">
                    <strong>IMPORTANT: SUBSCRIPTION WARNING</strong>
                    <p>
                        Deleting your account or uninstalling the app does <strong>NOT</strong> cancel your Google Play subscription.
                        You will continue to be charged unless you cancel it in the Play Store.
                    </p>
                    <a 
                        href="https://play.google.com/store/account/subscriptions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        Manage Google Play Subscriptions &rarr;
                    </a>
                </div>

                {/* Section 1: Account Deletion */}
                <div className="deletion-section">
                    <h2>Delete My Account</h2>
                    
                    {message && (
                        <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                            {message}
                        </div>
                    )}
                    
                    {error && (
                        <div className="error-message" style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                            {error}
                        </div>
                    )}

                    {user ? (
                        <>
                            <p>You are currently logged in as <strong>{user.email || 'User'}</strong>.</p>
                            <p>Once you delete your account, you will lose all profile data, matches, and messages permanently.</p>
                            <button 
                                className="delete-btn-large" 
                                onClick={deleteUserAccount}
                                disabled={isLoading}
                            >
                                {isLoading ? <ClipLoader size={20} color="#fff" /> : "Delete My Account"}
                            </button>
                        </>
                    ) : (
                        <>
                            <p>If you are unable to log in, please fill out the form below to request account deletion.</p>
                            <form className="deletion-form" onSubmit={handleRequestDeletion}>
                                <input 
                                    type="email" 
                                    placeholder="Your Account Email" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required 
                                    disabled={isLoading}
                                />
                                <textarea 
                                    placeholder="Reason for deletion (optional)" 
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    disabled={isLoading}
                                />
                                <button type="submit" className="submit-request-btn" disabled={isLoading}>
                                    {isLoading ? <ClipLoader size={20} color="#24252a" /> : "Send Deletion Request"}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {/* Section 3: Contact & Legal */}
                <div className="contact-section">
                    <h2>Contact & Support</h2>
                    <p><strong>Brather Ltd</strong> (Trading as Flintt.me)</p>
                    <p>48 The Boulevard, Horsham, England, RH12 1EP</p>
                    <p>Email: <a href="mailto:info@flintt.me">info@flintt.me</a></p>
                    
                    <div className="legal-links">
                        <Link to="/terms-of-service">Terms of Service</Link> | <Link to="/privacy-policy">Privacy Policy</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportDataDeletion;
